import { useRef } from 'react';
import type { GetTokenSilentlyOptions, LogoutOptions } from '@auth0/auth0-react';
import type { PostHog } from 'posthog-js';
import { getLearnosityInitObject, type LearnosityInitializationParams } from '../bffApiCalls/getLearnosityInitObject';
import type { LearnosityFetchOptions } from '../bffApiCalls/fetchLearnosityStartupData';
import type { LearnosityRequest } from '../models/LearnosityRequest';
import type { LearnosityInitializationRefetch } from './useLearnosityItemsInitialization';

/** Keep the signing source with its response, including generated preview session IDs. */
export function useLearnosityBffInitialization(
  getAccessTokenSilently: (options: GetTokenSilentlyOptions) => Promise<string>,
  logout: (options?: LogoutOptions) => Promise<void>,
  posthog: PostHog,
) {
  const sources = useRef(new WeakMap<LearnosityRequest, LearnosityInitializationParams>());

  const fetchInitialization = async (params: LearnosityInitializationParams, options: LearnosityFetchOptions = {}) => {
    const snapshot = structuredClone(params);
    const initialization = await getLearnosityInitObject(snapshot, getAccessTokenSilently, logout, posthog, options);
    sources.current.set(initialization, snapshot);
    return initialization;
  };

  const refetchInitialization: LearnosityInitializationRefetch = (initialization, signal) => {
    const params = sources.current.get(initialization);
    if (!params) return Promise.reject(new Error('Learnosity initialization source unavailable'));
    return getLearnosityInitObject(params, getAccessTokenSilently, logout, posthog, { signal, captureFailure: false });
  };

  return { fetchInitialization, refetchInitialization };
}
